import React, { useMemo } from "react";
import * as R from "ramda";
import { css, compose } from "glamor";
import ReactTable, { ReactTableDefaults } from "react-table";
import selectTableHOC from "react-table/lib/hoc/selectTable";
import "react-table/react-table.css";

import Button from "../Button";

const defaultStyles = {
    table: css({
        border: "none !important",
    }),
    thead: css({
        background: "#fafafa",
        borderBottom: "1px solid #e8e8e8",
        borderTop: "1px solid #e8e8e8",
        boxShadow: "none !important",
        fontWeight: "bold",
    }),
    theadTh: css({
        border: "none !important",
        padding: "13px 10px !important",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
    }),
    td: css({
        borderRight: "none !important",
        padding: "13px 10px !important",
    }),
    emptyTrGroup: css({
        borderBottom: "none !important",
    }),
    pagination: css({
        border: "none !important",
        boxShadow: "none !important",
        marginTop: "1rem",
    }),
};

const propGetters = styles => ({
    getTheadProps: () => ({
        className: `${styles.thead}`,
    }),
    getTheadThProps: () => ({
        className: `${styles.theadTh}`,
    }),
    getTdProps: () => ({
        className: `${styles.td}`,
    }),
    getTrGroupProps: props => ({
        className: props.data.length ? "" : `${styles.emptyTrGroup}`,
    }),
    getPaginationProps: () => ({
        className: `${styles.pagination}`,
    }),
    getTableProps: () => ({
        className: `${styles.table}`,
    }),
});

// merges default className with custom className, all another keys are custom only
const mergedPropsThunk = (...propsThunks) => (tableProps, rowProps, column) => {
    const executedThunks = R.map(thunk =>
        thunk ? thunk(tableProps, rowProps, column) : {}
    )(propsThunks);
    const mergedThunks = R.mergeAll(executedThunks);
    return {
        ...mergedThunks,
        className: R.map(thunk => thunk.className)(executedThunks),
    };
};

const writeStyles = stylesProp => {
    const mergeCSS = (val, key) => {
        if (key in stylesProp) {
            return compose(val, stylesProp[key]);
        }
        return val;
    };
    return R.mapObjIndexed(mergeCSS, defaultStyles);
};

const shouldShowPagination = ({
    data,
    showPagination,
    pageSize,
    defaultPageSize = ReactTableDefaults.defaultPageSize,
    pages,
}) => {
    if (typeof pages === "number" && pages > 1) {
        return true;
    }
    if (typeof showPagination === "boolean" && !showPagination) {
        return false;
    }
    const isControlled = typeof pageSize === "number";
    if (isControlled) {
        return true;
    }
    const itemsCount = Array.isArray(data) ? data.length : 0;
    return itemsCount > defaultPageSize;
};

const withTableRef = Table => {
    return ({ tableRef, ...rest }) => {
        return <Table {...rest} ref={tableRef} />;
    };
};

const Table = props => {
    const styles = writeStyles(props.styles);
    const mergedPropGetters = R.mapObjIndexed((getter, key) =>
        mergedPropsThunk(getter, props[key])
    )(propGetters(styles));

    const rest = R.omit(R.keys(mergedPropGetters), props);
    const showPagination = shouldShowPagination(rest);

    const MemoizedTable = useMemo(
        () =>
            withTableRef(
                props.selectable ? selectTableHOC(ReactTable) : ReactTable
            ),
        [props.selectable]
    );

    return (
        <MemoizedTable
            className={`${styles.table}`}
            PreviousComponent={props => (
                <Button.Primary {...props}>Previous</Button.Primary>
            )}
            NextComponent={props => (
                <Button.Primary {...props}>Next</Button.Primary>
            )}
            noDataText="No results found"
            showPageJump={false}
            showPageSizeOptions={false}
            {...mergedPropGetters}
            {...rest}
            showPagination={showPagination}
        />
    );
};

Table.defaultProps = {
    styles: {},
};

export default Table;
